import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { Task } from '@/types/task';
import type { SeasonTaskStats } from '@/services/dashboard-service';
import { getTaskStatus, formatCompactDueDate } from '@/utils/task-display-utils';
import { localTodayISO } from '@/utils/date-utils';
import { Card } from '@/components/common/Card';
import { ExpandChevron } from '@/components/common/ExpandChevron';
import { OperationalRow } from '@/components/common/OperationalRow';
import { QuickCompleteCheckbox } from '@/components/common/QuickCompleteCheckbox';
import { deriveSurfacedTasks, deriveStatusLineCounts } from './consolidated-prep-utils';

// ─────────────────────────────────────────────────────────────────────────────
// ConsolidatedPrepCard — DASH-P1 / M97 (Closes #787)
//
// One presentational prep card built on the shared F2 `Card` shell. It consumes
// the SAME props PrepCenter receives (consume-only contract) plus `taskStats`:
//   - done/total numerator  → `taskStats.completed` / `taskStats.total` (the SSOT
//     `liveTaskStats` numerator, F-1). NO parallel count over the `tasks` prop.
//   - row stripes + status-line breakdown counts → the SINGLE `getTaskStatus`
//     classifier (via consolidated-prep-utils), so the two surfaces agree.
//
// The card is presentational: it derives everything from its props on each render
// and holds NO store subscription. DashboardPage re-feeds fresh `tasks` after a
// quick-complete (task-store reload + subscription), so check-off/unblock
// recompute "for free" on the next render.
// ─────────────────────────────────────────────────────────────────────────────

interface ConsolidatedPrepCardProps {
  /** Season-filtered, open (non-completed / non-snoozed) tasks — same list PrepCenter gets. */
  tasks: Task[];
  /** Active season id — drives the View-all link target. */
  seasonId?: string;
  /** Optimistic async quick-complete handler. */
  onQuickComplete?: (id: string) => Promise<void>;
  /** Inline error messages keyed by task id. */
  taskErrors?: Record<string, string>;
  /** SSOT reactive task stats (liveTaskStats). Supplies the done/total numerator. */
  taskStats?: SeasonTaskStats;
}

function getToday(): string {
  return localTodayISO();
}

export function ConsolidatedPrepCard({
  tasks,
  seasonId,
  onQuickComplete,
  taskErrors,
  taskStats,
}: ConsolidatedPrepCardProps) {
  const [expanded, setExpanded] = useState(false);

  const today = getToday();

  // ── Empty state: no tasks for the selected season ──────────────────────────
  // total === 0 (or no stats at all) means there is nothing to prep yet.
  if (!taskStats || taskStats.total === 0) {
    return (
      <Card className="px-5 py-4" data-testid="consolidated-prep-card" data-empty="true">
        <p
          className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)] mb-2"
          data-testid="consolidated-prep-kicker"
        >
          Prep · ready to begin?
        </p>
        <Link
          to="/templates"
          className="text-[12px] text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] transition-colors"
          data-testid="consolidated-prep-browse-templates"
        >
          Browse templates →
        </Link>
      </Card>
    );
  }

  // ── Completed state (#849 subsumption): all tasks done, open list is empty ─
  // When tasks.length === 0 but total > 0 and completed === total, the season
  // checklist is fully done (no remaining open tasks). Distinct from empty state
  // (total === 0) — do NOT conflate the two. This gate exactly mirrors PrepCenter's
  // `taskStats.total > 0 && taskStats.completed === taskStats.total` condition so the
  // completed-state contract is fully subsumed and cannot silently regress.
  if (tasks.length === 0 && taskStats.total > 0 && taskStats.completed === taskStats.total) {
    const viewAllHref = seasonId ? `/prep/tasks?season=${seasonId}` : '/seasons';
    const total = taskStats.total;
    return (
      <Card className="px-5 py-4" data-testid="consolidated-prep-card" data-completed="true">
        <p
          className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)] mb-2"
          data-testid="consolidated-prep-kicker"
        >
          Prep · All done
        </p>
        <p
          className="text-[14px] font-medium text-[var(--color-text-primary)] mb-2"
          data-testid="consolidated-prep-complete-label"
        >
          All {total} task{total !== 1 ? 's' : ''} complete for this season
        </p>
        <Link
          to={viewAllHref}
          className="text-[11px] text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] transition-colors"
          data-testid="consolidated-prep-complete-link"
        >
          View Task List →
        </Link>
        {/* M97 / #706: append a second checklist to this populated (fully-complete)
            season. Routes to the Templates path with the season preselected so the
            user picks a DISTINCT template via the existing picker (Option A). Guarded
            on seasonId — the preselect carry-through requires it. */}
        {seasonId && (
          <Link
            to={`/prep/templates?season=${seasonId}`}
            className="block mt-2 text-[11px] text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] transition-colors"
            data-testid="consolidated-prep-add-checklist"
          >
            Add another checklist →
          </Link>
        )}
      </Card>
    );
  }

  const done = taskStats.completed;
  const total = taskStats.total;
  const allDone = done >= total;
  const progressPercent = total > 0 ? Math.min(100, Math.max(0, (done / total) * 100)) : 0;

  const { overdue, open, nextDueLeadDays } = deriveStatusLineCounts(tasks, today);
  const surfaced = deriveSurfacedTasks(tasks, today);

  const viewAllHref = seasonId ? `/prep/tasks?season=${seasonId}` : '/seasons';

  return (
    <Card
      className="px-5 py-4"
      data-testid="consolidated-prep-card"
      data-task-count-done={done}
      data-task-count-total={total}
      data-overdue-count={overdue}
      data-open-count={open}
    >
      {/* Header / toggle row — the button owns aria-expanded (F3 button-owns-state) */}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3"
        data-testid="consolidated-prep-toggle"
      >
        <span
          className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]"
          data-testid="consolidated-prep-kicker"
        >
          Prep
        </span>
        <ExpandChevron expanded={expanded} className="text-[var(--color-text-muted)]" />
      </button>

      {/* Progress bar */}
      <div
        className="mt-3 h-1.5 w-full rounded-full bg-[var(--color-border-subtle)] overflow-hidden"
        data-testid="consolidated-prep-progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
      >
        <div
          className="h-full bg-[var(--color-accent)]"
          style={{ width: `${progressPercent}%` }}
          data-slot="progress-fill"
        />
      </div>

      {/* Numerator + one-line status */}
      <div className="mt-2 flex items-baseline justify-between gap-3">
        <span
          className="text-[14px] font-medium text-[var(--color-text-primary)]"
          data-testid="consolidated-prep-count"
        >
          {done}/{total}
        </span>

        {allDone ? (
          <span
            className="text-[11px] text-[var(--color-text-muted)]"
            data-testid="consolidated-prep-status-line"
          >
            All {total} tasks complete
          </span>
        ) : (
          <span
            className="text-[11px] text-[var(--color-text-muted)]"
            data-testid="consolidated-prep-status-line"
          >
            {overdue > 0 && (
              <span style={{ color: 'var(--color-overdue)' }} data-testid="consolidated-prep-overdue-segment">
                {overdue} overdue
              </span>
            )}
            {overdue > 0 && ' · '}
            {open} open
            {' · '}
            {nextDueLeadDays !== null
              ? `next due in ${nextDueLeadDays}d`
              : 'no upcoming due dates'}
          </span>
        )}
      </div>

      {/* Expanded: surfaced rows + inline check-off + view-all */}
      {expanded && (
        <div className="mt-3" data-testid="consolidated-prep-expanded">
          <div className="rounded-md border border-[var(--color-border)] overflow-hidden">
            {surfaced.map((task) => {
              const status = getTaskStatus(task, today);
              const errorMessage = taskErrors?.[task.id];
              return (
                <OperationalRow key={task.id} status={status} data-testid="consolidated-prep-row">
                  {/* Checkbox is a SIBLING of the content, never nested (TaskRow pattern) */}
                  {onQuickComplete && (
                    <div data-testid="consolidated-prep-row-checkbox">
                      <QuickCompleteCheckbox task={task} onComplete={onQuickComplete} size="sm" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 flex items-start gap-2 px-3 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className="truncate text-[14px] font-medium text-[var(--color-text-primary)]"
                          data-testid="consolidated-prep-row-title"
                        >
                          {task.title}
                        </p>
                        <span className="text-[11px] text-[var(--color-text-muted)] flex-shrink-0">
                          {formatCompactDueDate(task, new Date(`${today}T00:00:00.000Z`))}
                        </span>
                      </div>
                      {errorMessage && (
                        <p
                          className="text-[11px] text-[var(--color-accent-hover)] mt-0.5"
                          data-testid="consolidated-prep-row-error"
                        >
                          {errorMessage}
                        </p>
                      )}
                    </div>
                  </div>
                </OperationalRow>
              );
            })}
          </div>

          <Link
            to={viewAllHref}
            className="mt-2 inline-block text-[11px] text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] transition-colors"
            data-testid="consolidated-prep-view-all"
          >
            View all {total} tasks →
          </Link>
        </div>
      )}
    </Card>
  );
}
